import React, { useEffect, useState } from "react"
import { Redirect } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck } from "@fortawesome/free-solid-svg-icons"
import { useRights } from "../../../hooks/rights"
import { fetchAutreEntites } from "../../../store/actions/autreActions"
import {
	postEntite,
	postEntiteReset,
} from "../../../store/actions/entiteActions"
import { getBaseAdresse } from "../../../utilities/defaultObjects"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import Loader from "../../basics/Loader"
import CustomModal from "../../basics/interface/CustomModal"
import ConfigurationForm from "./ConfigurationForm"

const styles = {
	shell: (tab) => ({
		width: "100%",
		maxWidth:
			tab === "rh" ||
			tab === "entite" ||
			tab === "paiements" ||
			tab === "depannage" ||
			tab === "emails" ||
			tab === "vehicule" ||
			// Matrice catégories × groupes : autant de colonnes que de groupes.
			tab === "notifications"
				? 1100
				: 720,
		margin: "0 auto",
		transition: "max-width 0.25s ease",
	}),
	header: { marginBottom: 28 },
	subtitle: { fontSize: 14, color: COLORS.textMuted, marginTop: 6 },
}

// Build the multipart payload exactly like the legacy ClientsForm does, so the
// full entity (adresses/personnes/fournitures…) is preserved on save — we only
// edit a few base fields but must never wipe the rest. La flotte (`vehicules`)
// est volontairement EXCLUE : elle se gère par véhicule via
// api/configuration/vehicule. EntiteView ne reçoit aucun `vehicules[...]` → il
// laisse le parc intact.
const buildEntiteFormData = (entite) => {
	const formData = new FormData()
	for (let [k, v] of Object.entries(entite)) {
		if (k === "vehicules") {
			continue
		} else if (
			["adresses", "personnes", "fournitures", "extra_data"].includes(k)
		) {
			formData.append(k, JSON.stringify(v))
		} else {
			formData.append(k, v)
		}
	}
	return formData
}

const Configuration = () => {
	const { hasRight } = useRights()
	const dispatch = useDispatch()
	const masterEntite = useSelector((s) => s.masterReducer.masterEntite)
	const postState = useSelector((s) => s.entiteInfo.postEntite)
	const [entite, setEntite] = useState(null)
	const [modalOpen, setModalOpen] = useState(false)
	// L'onglet initial est corrigé par ConfigurationForm si l'utilisateur n'a pas
	// droit à « Identité » (qui est boss-only) — il connaît la liste des onglets.
	const [tab, setTab] = useState("identite")

	// Load the master entity into LOCAL state — decoupled from the shared
	// `editEntite` redux slice that the legacy ClientsForm drives.
	useEffect(() => {
		if (masterEntite && masterEntite.id) setEntite({ ...masterEntite })
	}, [masterEntite && masterEntite.id])

	useEffect(() => {
		// masterEntite is already loaded app-wide by NavV2 on mount, so we don't
		// fetch here (that caused a duplicate autres_entites call). Just reset
		// the save state on unmount.
		return () => dispatch(postEntiteReset())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Once the save succeeds, keep the confirmation on screen for a beat, then
	// reset the post state and close the modal.
	useEffect(() => {
		if (!postState.saved) return
		const t = setTimeout(() => {
			dispatch(postEntiteReset())
			setModalOpen(false)
		}, 1000)
		return () => clearTimeout(t)
	}, [postState.saved, dispatch])

	// La page s'ouvre à quiconque a AU MOINS un onglet (module dérivé
	// `configuration`) ; le détail onglet par onglet est dans ConfigurationForm,
	// et chaque endpoint de config a son propre garde serveur.
	if (!hasRight("configuration")) return <Redirect to="/home" />

	const update = (key, value) =>
		setEntite((prev) => ({ ...prev, [key]: value }))

	const updateExtraData = (key, value) =>
		setEntite((prev) => ({
			...prev,
			extra_data: { ...(prev.extra_data || {}), [key]: value },
		}))

	// Patch d'un champ de tarif dans extra_data.tarification[section][key].
	const updateTarification = (section, key, value) =>
		setEntite((prev) => {
			const extra = prev.extra_data || {}
			const tarif = extra.tarification || {}
			return {
				...prev,
				extra_data: {
					...extra,
					tarification: {
						...tarif,
						[section]: { ...(tarif[section] || {}), [key]: value },
					},
				},
			}
		})

	// `whole` replaces the entire adresse (autocomplete fills); otherwise a
	// single field is patched.
	const updateAdresse = (index, key, value, whole = false) =>
		setEntite((prev) => ({
			...prev,
			adresses: prev.adresses.map((a, i) =>
				i !== index ? a : whole ? value : { ...a, [key]: value },
			),
		}))

	const addAdresse = () =>
		setEntite((prev) => ({
			...prev,
			adresses: [...(prev.adresses || []), getBaseAdresse()],
		}))

	const confirmSave = () =>
		dispatch(postEntite(buildEntiteFormData(entite))).then(() =>
			dispatch(fetchAutreEntites()),
		)

	const closeModal = () => {
		setModalOpen(false)
		dispatch(postEntiteReset())
	}

	const busy = postState.saving || postState.saved

	return (
		<>
			{modalOpen && (
				<CustomModal
					title="Enregistrer les modifications ?"
					onClose={closeModal}
					showCloseButton={!busy}
					buttons={[
						{
							title: "Non",
							styleName: "secondary",
							onClick: closeModal,
							disabled: busy,
						},
						{
							title: postState.saving ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : postState.saved ? (
								<FontAwesomeIcon icon={faCheck} />
							) : (
								"Oui"
							),
							styleName: "primary",
							onClick: confirmSave,
							disabled: busy,
						},
					]}
				>
					Voulez-vous enregistrer les modifications de la
					configuration ?
				</CustomModal>
			)}
			<div style={gs.page}>
				<div style={styles.shell(tab)}>
					<div style={styles.header}>
						<h2 style={gs.title}>Configuration</h2>
						<div style={styles.subtitle}>
							Paramètres de l'entreprise
						</div>
					</div>
					{!entite ? (
						<Loader
							variant="ring"
							message="Chargement de la configuration…"
						/>
					) : (
						<ConfigurationForm
							entite={entite}
							update={update}
							updateAdresse={updateAdresse}
							addAdresse={addAdresse}
							updateTarification={updateTarification}
							updateExtraData={updateExtraData}
							onRequestSave={() => setModalOpen(true)}
							saving={postState.saving}
							saved={postState.saved}
							tab={tab}
							setTab={setTab}
						/>
					)}
				</div>
			</div>
		</>
	)
}

export default Configuration
